import { useCallback, useEffect, useRef, useState } from 'react'

export function useScrollToBottom() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const isAtBottomRef = useRef(true)

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const onScroll = () => {
      const threshold = 40
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold
      isAtBottomRef.current = atBottom
      setIsAtBottom(atBottom)
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Auto-scroll when new content is added and user is at bottom.
  // The observer stays connected permanently and reads the latest
  // isAtBottom value via ref to avoid stale-closure issues.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new MutationObserver(() => {
      if (isAtBottomRef.current) {
        el.scrollTop = el.scrollHeight
      }
    })

    observer.observe(el, { childList: true, subtree: true, characterData: true })
    return () => observer.disconnect()
  }, [])

  return { containerRef, isAtBottom, scrollToBottom }
}
